import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPlay } from '@fortawesome/free-solid-svg-icons';
import DemoModal from './DemoModal';

const HeroSection = ({ onDemoClick, onPurchaseClick }) => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const ctaRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const slides = [
    {
      title: "智能财务管理",
      subtitle: "专为物流快运行业打造的智能财务解决方案",
      description: "自动化凭证生成，实时数据同步，让财务管理更简单高效",
      image: "/manage.jpg",
      cta: "立即体验"
    },
    {
      title: "数据驱动决策",
      subtitle: "实时财务数据分析，助力企业精准决策",
      description: "多维度财务报表，智能预警系统，让每一个决策都有数据支撑",
      image: "/data.jpg",
      cta: "了解更多"
    },
    {
      title: "安全可靠",
      subtitle: "银行级安全保障，数据加密存储",
      description: "多重安全防护，确保您的财务数据安全无忧",
      image: "/safe.jpg",
      cta: "安全保障"
    }
  ];

  useEffect(() => {
    // 初始动画
    const tl = gsap.timeline();
    
    tl.fromTo(
      titleRef.current.children,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.1, ease: 'power2.out' }
    )
    .fromTo(
      subtitleRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
      '-=0.4'
    )
    .fromTo(
      ctaRef.current.children,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, stagger: 0.1, ease: 'power2.out' },
      '-=0.3'
    );

    // 自动轮播
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // 切换幻灯片时的动画
    const tl = gsap.timeline();
    
    tl.to(titleRef.current.children, {
      y: -20,
      opacity: 0,
      duration: 0.3,
      stagger: 0.05
    })
    .to(subtitleRef.current, {
      y: -20,
      opacity: 0,
      duration: 0.3
    }, '-=0.2')
    .set(titleRef.current.children, { y: 20 })
    .set(subtitleRef.current, { y: 20 })
    .to(titleRef.current.children, {
      y: 0,
      opacity: 1,
      duration: 0.5,
      stagger: 0.05,
      ease: 'power2.out'
    })
    .to(subtitleRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.5,
      ease: 'power2.out'
    }, '-=0.3');
  }, [currentSlide]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section ref={heroRef} className="relative h-screen overflow-hidden">
      {/* 背景图片轮播 */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/80 via-primary-dark/60 to-transparent"></div>
          </div>
        ))}
      </div>

      {/* 内容区域 */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            
            {/* 标题 */}
            <h2 ref={titleRef} className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {slides[currentSlide].title.split('').map((char, index) => (
                <span key={index} className="inline-block">
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </h2>
            
            {/* 副标题 */}
            <p ref={subtitleRef} className="text-xl md:text-2xl text-blue-100 mb-4 font-medium">
              {slides[currentSlide].subtitle}
            </p>
            
            {/* 描述 */}
            <p className="text-lg text-blue-200 mb-8 leading-relaxed">
              {slides[currentSlide].description}
            </p>
            
            {/* CTA按钮 */}
            <div ref={ctaRef} className="flex flex-row gap-4 justify-center">
              <button 
                onClick={onPurchaseClick}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-10 py-5 rounded-lg font-bold text-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl flex items-center justify-center group"
              >
                立即购买
                <FontAwesomeIcon 
                  icon={faArrowRight} 
                  className="ml-3 text-xl group-hover:translate-x-1 transition-transform" 
                />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className="border-2 border-white/70 text-white/90 px-6 py-3 rounded-lg font-medium hover:bg-white/10 hover:border-white transition-all duration-300 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2 text-sm" />
                预约演示
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 轮播指示器 */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-primary-cyan scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </div>

      {/* 滚动提示 */}
      <div className="absolute bottom-8 right-8 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
      
      {/* 演示预约弹窗 */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
    </section>
  );
};

export default HeroSection;